import { useState, useEffect, useCallback } from 'react';
import { auditApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { MagnifyingGlass, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const getActionBadge = (action) => {
    const colors = {
        create: 'bg-green-100 text-green-800',
        update: 'bg-blue-100 text-blue-800',
        delete: 'bg-red-100 text-red-800',
        login: 'bg-purple-100 text-purple-800',
        logout: 'bg-gray-100 text-gray-800',
        start: 'bg-amber-100 text-amber-800',
        complete: 'bg-green-100 text-green-800',
        approve: 'bg-green-100 text-green-800',
        reopen: 'bg-amber-100 text-amber-800',
        upload: 'bg-blue-100 text-blue-800',
        validate: 'bg-green-100 text-green-800'
    };
    return <Badge className={colors[action] || 'bg-muted text-muted-foreground'}>{action}</Badge>;
};

const ENTITY_LABELS = {
    user: 'Utente',
    customer: 'Cliente',
    contract: 'Contratto',
    contract_node: 'Step Workflow',
    document: 'Documento',
    workflow_template: 'Template Workflow'
};

export default function AuditLogPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entityFilter, setEntityFilter] = useState('all');
    const [search, setSearch] = useState('');

    const loadLogs = useCallback(async () => {
        try {
            const params = {};
            if (entityFilter !== 'all') {
                params.entity_type = entityFilter;
            }
            const response = await auditApi.getAll(params);
            setLogs(response.data);
        } catch (error) {
            console.error('Error loading audit logs:', error);
            toast.error('Errore nel caricamento dei log');
        } finally {
            setLoading(false);
        }
    }, [entityFilter]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const filteredLogs = logs.filter(log => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            log.action?.toLowerCase().includes(searchLower) ||
            log.entity_type?.toLowerCase().includes(searchLower) ||
            log.entity_id?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Cerca nei log..."
                        data-testid="audit-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                    <SelectTrigger className="w-48" data-testid="entity-filter">
                        <SelectValue placeholder="Entità" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutte le entità</SelectItem>
                        <SelectItem value="user">Utenti</SelectItem>
                        <SelectItem value="customer">Clienti</SelectItem>
                        <SelectItem value="contract">Contratti</SelectItem>
                        <SelectItem value="contract_node">Step Workflow</SelectItem>
                        <SelectItem value="document">Documenti</SelectItem>
                        <SelectItem value="workflow_template">Templates</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Logs Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-header">
                                <TableHead>Data/Ora</TableHead>
                                <TableHead>Azione</TableHead>
                                <TableHead>Entità</TableHead>
                                <TableHead>ID Entità</TableHead>
                                <TableHead>Dettagli</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredLogs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-12">
                                        <ClockCounterClockwise className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Nessun log trovato</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredLogs.map((log) => (
                                    <TableRow 
                                        key={log.id} 
                                        className="table-row"
                                        data-testid={`audit-row-${log.id}`}
                                    >
                                        <TableCell className="table-cell text-muted-foreground">
                                            {formatDateTime(log.created_at)}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {getActionBadge(log.action)}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <Badge variant="outline">
                                                {ENTITY_LABELS[log.entity_type] || log.entity_type}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="table-cell font-mono text-xs text-muted-foreground">
                                            {log.entity_id?.substring(0, 8)}...
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {log.payload && Object.keys(log.payload).length > 0 && (
                                                <code className="text-xs bg-muted px-2 py-1 rounded">
                                                    {JSON.stringify(log.payload).substring(0, 50)}...
                                                </code>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
